import type { FunctionComponent } from 'preact';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';

import SearchItem from 'Components/searchItem/index';
import type { RepeaterFunctionInvoker, TemplateFunction } from 'Components/types';
import useScroll, { type UseScroll } from 'Core/hooks/horizontalScroll';
import { prepareResponseFacets } from 'Core/reducers/search/response';
import { createSelectedVehicleSelectionSelector } from 'Core/selectors/fitmentSearch/index';
import { productDataSelector } from 'Core/selectors/product';
import { useSelector } from 'Core/store';
import { SearchItemData, SearchItemDataCollection, type SelectionEntry } from 'Models/index';
import type { SearchItemTemplateParams } from 'Models/searchItemData';
import productConfig from 'Models/uiConfig/productConfig';
import type { RequestExtra } from 'Models/uiConfig/requestTypes';
import { getUriFromRequest } from 'Modules/converter/index';
import { search } from 'Modules/serverApi/index';
import type { ServerModel } from 'Modules/serverApi/types';
import { cloneSafe } from 'Utils/components';
import { isTruthy } from 'Utils/guards';
import { safeCallback } from 'Utils/safeCallback';

type Params = {
  widgetName: string;
  items: RepeaterFunctionInvoker<SearchItemTemplateParams>;
  searchLink: string;
} & UseScroll;

type Props = {
  template: TemplateFunction<Params>;
  name: string;
  count?: number;
  requestExtra?: RequestExtra;
  sort?: string;
  useManualItems?: true;
  getSelection?: (itemData: ServerModel.SearchItem) => SelectionEntry[] | undefined;
  onItemsRendered?: () => void;
};

const RelatedItems: FunctionComponent<Props> = ({
  template,
  name: widgetName,
  count,
  requestExtra,
  sort,
  useManualItems,
  getSelection,
  onItemsRendered,
}) => {
  const rootRef = useRef<HTMLElement>(null);

  const { scrollLeft, scrollRight, isLeftBorder, isRightBorder } = useScroll(rootRef);

  const { localItemId }: { localItemId?: string } = productConfig;

  const productData = useSelector(productDataSelector);

  const vehicleSelection = useSelector(createSelectedVehicleSelectionSelector);
  const colorsSelection = useMemo(
    () => (productData?._Color?.length ? productData._Color.toFacetValues('_Color') : []),
    [productData],
  );

  const [customRequestSelection, setCustomRequestSelection] = useState<SelectionEntry[]>([]);
  const [relatedItems, setRelatedItems] = useState<SearchItemDataCollection>();

  const handleResponse = useCallback(
    (response: ServerModel.SearchResponse) => {
      setRelatedItems(
        new SearchItemDataCollection(
          response.Items.filter(
            (item) => useManualItems || `${SearchItemData.getFromRaw(item, 'id')}` !== `${localItemId}`,
          ).slice(0, count),
          'related',
        ),
      );
    },
    [count, localItemId, useManualItems],
  );

  useEffect(() => {
    // send a request without facet selection to make a response on the server, e.g. in plugins
    if (useManualItems && count) {
      search({
        selection: vehicleSelection,
        pageSize: count,
        extra: { itemId: localItemId, mode: 'RelatedItems', ...(requestExtra || {}) },
      }).then((response: ServerModel.SearchResponse) => {
        handleResponse(response);

        if (response.Facets.length) {
          setCustomRequestSelection(prepareResponseFacets(response.Facets, []).selection);
        }
      });
    }
  }, [count, localItemId, handleResponse, useManualItems, vehicleSelection, requestExtra]);

  useEffect(() => {
    if (productData && Object.keys(productData).length && getSelection && count) {
      let customSelection = getSelection(productData);

      if (!Array.isArray(customSelection)) {
        // don't let unknown things be added to the request
        customSelection = [];
      } else if (customSelection.length) {
        setCustomRequestSelection(customSelection);
      }

      const selection = [...customSelection, ...vehicleSelection, ...colorsSelection];

      search({
        selection,
        pageSize: count,
        sort,
        extra: { itemId: localItemId, ...(requestExtra || {}) },
      }).then((response: ServerModel.SearchResponse) => handleResponse(response));
    }
  }, [
    count,
    getSelection,
    localItemId,
    productData,
    handleResponse,
    sort,
    vehicleSelection,
    colorsSelection,
    requestExtra,
  ]);

  useEffect(() => {
    if (relatedItems?.length) {
      safeCallback(onItemsRendered, 'related_items_on_items_rendered', { component: 'RelatedItems' })();
    }
  }, [relatedItems, onItemsRendered]);

  if (!relatedItems?.length) {
    return null;
  }

  const { href: searchLink } = getUriFromRequest(
    {
      selection: [
        ...customRequestSelection,
        ...(useManualItems ? [] : vehicleSelection),
        ...colorsSelection,
      ].filter(isTruthy),
    },
    { goToSearchPage: true },
  );

  const items = relatedItems ? relatedItems.repeaterComponents(SearchItem) : [];

  const component = template.call({
    widgetName,
    items,
    searchLink,
    scrollLeft,
    scrollRight,
    isLeftBorder,
    isRightBorder,
  });

  return cloneSafe(component, rootRef);
};

export default RelatedItems;
